import { Controller } from "@hotwired/stimulus"

// Opens the structured data inside a log line — SQL bind parameters, a
// Parameters: dump, a Sidekiq payload — in a dialog, laid out over several lines
// instead of crammed into one.
//
// One dialog serves the whole page: the payload travels on the clicked element,
// so a log of a thousand lines does not become a thousand dialogs.
export default class extends Controller {
    static targets = ["output"]

    static values = { dialog: String }

    open(event) {
        const payload = event.currentTarget.dataset.payload
        if (!payload) return

        this.outputTarget.textContent = this.format(payload)
        this.dialog?.showModal()
    }

    async copy(event) {
        try {
            await navigator.clipboard.writeText(this.outputTarget.textContent)
            this.confirm(event.currentTarget)
        } catch {
            // No clipboard permission, or an insecure origin. The text is on
            // screen and selectable either way, so this is not worth an alert.
        }
    }

    // JSON gets the real thing. Everything else is Ruby's inspect output —
    // {"a" => 1}, [["x", true]] — which never parses, so it is only broken
    // across lines at the punctuation. What shows up stays recognisably the text
    // that is in the log rather than a translation of it.
    format(payload) {
        try {
            return JSON.stringify(JSON.parse(payload), null, 2)
        } catch {
            return this.indent(payload)
        }
    }

    indent(text) {
        let out = ""
        let depth = 0
        let quote = null
        let escaped = false
        let afterBreak = false

        for (const char of text) {
            if (quote) {
                out += char
                if (escaped) escaped = false
                else if (char === "\\") escaped = true
                else if (char === quote) quote = null
                continue
            }

            // The space that followed the comma or brace we just broke on.
            if (afterBreak && char === " ") continue
            afterBreak = false

            if (char === '"' || char === "'") {
                quote = char
                out += char
            } else if (char === "{" || char === "[") {
                depth += 1
                out += `${char}\n${"  ".repeat(depth)}`
                afterBreak = true
            } else if (char === "}" || char === "]") {
                depth = Math.max(depth - 1, 0)
                out += `\n${"  ".repeat(depth)}${char}`
            } else if (char === ",") {
                out += `,\n${"  ".repeat(depth)}`
                afterBreak = true
            } else {
                out += char
            }
        }

        // An empty container reads worse broken open than left alone.
        return out.replace(/\{\s*\}/g, "{}").replace(/\[\s*\]/g, "[]")
    }

    confirm(button) {
        const original = button.textContent
        button.textContent = "Kopiert"
        setTimeout(() => { button.textContent = original }, 1500)
    }

    get dialog() {
        return document.getElementById(this.dialogValue)
    }
}
